import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ChevronRight, ChevronLeft, ChevronDown, Building2, Plus, Medal, Mail, Clock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ClassificaOperatori, RICHIAMI_CATEGORIE as RICHIAMI_CATS } from "../components/ClassificaOperatori";
import DisciplinaPanel from "../components/DisciplinaPanel";

const TIPI = [
  { k: "Ritardo", label: "Ritardi", color: "bg-amber-100 text-amber-800 hover:bg-amber-200" },
  { k: "Ingresso non registrato", label: "Ingressi mancanti", color: "bg-red-100 text-red-800 hover:bg-red-200" },
  { k: "Dashboard non vista", label: "Dashboard non viste", color: "bg-blue-100 text-blue-800 hover:bg-blue-200" },
  { k: "Consegne non viste", label: "Consegne non viste", color: "bg-purple-100 text-purple-800 hover:bg-purple-200" },
  { k: "News non confermata", label: "News non confermate", color: "bg-orange-100 text-orange-800 hover:bg-orange-200" },
  { k: "Task non confermato", label: "Task non confermate", color: "bg-orange-100 text-orange-800 hover:bg-orange-200" },
  { k: "Task non effettuata", label: "Task non effettuate", color: "bg-rose-100 text-rose-800 hover:bg-rose-200" },
];
const longDate = (iso) => { if (!iso) return "—"; try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } };
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const meseLabel = (ym) => { const [y, m] = ym.split("-"); return `${MESI[Number(m) - 1] || ""} ${y}`; };
const monthBounds = (ym) => { const [y, m] = ym.split("-").map(Number); const last = new Date(y, m, 0).getDate(); return [`${ym}-01`, `${ym}-${String(last).padStart(2, "0")}`]; };
const shiftMese = (ym, delta) => { const [y, m] = ym.split("-").map(Number); const d = new Date(y, m - 1 + delta, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const monthOptionsList = () => { const now = new Date(); const opts = []; for (let i = 0; i < 18; i++) { const d = new Date(now.getFullYear(), now.getMonth() - i, 1); opts.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); } return opts; };
const MEDALS = [
  { card: "border-yellow-300 bg-yellow-50/60", num: "bg-yellow-100 border-yellow-300 text-yellow-800", icon: "#ca8a04", label: "Oro" },
  { card: "border-slate-300 bg-slate-50", num: "bg-slate-100 border-slate-300 text-slate-700", icon: "#64748b", label: "Argento" },
  { card: "border-amber-400 bg-amber-50/50", num: "bg-amber-100 border-amber-400 text-amber-900", icon: "#b45309", label: "Bronzo" },
];
const CMAP = { "Ritardo": "bg-amber-100 text-amber-800", "Ingresso non registrato": "bg-red-100 text-red-800", "Dashboard non vista": "bg-blue-100 text-blue-800", "Consegne non viste": "bg-purple-100 text-purple-800", "News non confermata": "bg-orange-100 text-orange-800", "Task non confermato": "bg-orange-100 text-orange-800", "Task non effettuata": "bg-rose-100 text-rose-800" };
const DOW_ABBR = ["", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const GMAP = { lieve: "bg-yellow-100 text-yellow-800", media: "bg-orange-100 text-orange-800", grave: "bg-red-100 text-red-800" };

export default function SupervisorRichiami() {
  const { selectedStructure, structures = [], can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mese, setMese] = useState(() => new Date().toISOString().slice(0, 7));
  const [da, a] = monthBounds(mese);
  const [detailOpen, setDetailOpen] = useState(false);
  const [dayView, setDayView] = useState(null);
  const qDa = dayView || da; const qA = dayView || a;
  const goMonth = (m) => { setMese(m); setDetailOpen(false); setDayView(null); };
  const structMap = Object.fromEntries((structures || []).map((s) => [s.id, s.nome]));

  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [form, setForm] = useState({ ids: [], data: new Date().toISOString().slice(0, 10), gravita: "media", descrizione: "" });
  const [expanded, setExpanded] = useState(null); // op_id espanso inline
  const [soloNC, setSoloNC] = useState(false);
  const [classDay, setClassDay] = useState(null);
  const todayIsoR = new Date().toISOString().slice(0, 10);
  const ieriIsoR = (() => { const d = new Date(Date.now() - 86400000); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const goDay = (iso) => { setMese(iso.slice(0, 7)); setClassDay(iso); };
  const [sollBusy, setSollBusy] = useState(false);
  const sollecitaTutti = async () => {
    if (!window.confirm(`Inviare l'email di sollecito presa visione a tutti gli operatori non confermati di ${meseLabel(mese)}? (solo chi ha un'email registrata)`)) return;
    setSollBusy(true);
    try {
      const { data: r } = await api.post("/supervisor/richiami/sollecita", { structure_id: selectedStructure, mese });
      let msg = `Sollecito inviato a ${r.inviati} operator${r.inviati === 1 ? "e" : "i"}`;
      if (r.senza_email?.length) msg += ` · ${r.senza_email.length} senza email`;
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ["sup-richiami"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setSollBusy(false);
  };

  const { data = { operatori: [], da: "", a: "" }, isLoading } = useQuery({
    enabled: detailOpen,
    queryKey: ["sup-richiami", selectedStructure, qDa, qA],
    queryFn: async () => (await api.get("/supervisor/richiami", { params: { structure_id: selectedStructure, da: qDa, a: qA } })).data,
  });
  const { data: meseData = { giorni: [], conteggi: {}, totale: 0 } } = useQuery({
    queryKey: ["sup-richiami-mese", selectedStructure, mese],
    queryFn: async () => (await api.get("/supervisor/richiami/mese", { params: { structure_id: selectedStructure, mese } })).data,
  });
  const ops = data.operatori || [];
  const shownOps = soloNC ? ops.filter((o) => (o.giorni_distinti - o.pv_giorni) > 0) : ops;
  const nNonConf = ops.filter((o) => (o.giorni_distinti - o.pv_giorni) > 0).length;
  const totale = ops.reduce((a, o) => a + (o.totale || 0), 0);

  const { data: allOps = [] } = useQuery({
    queryKey: ["sup-richiami-ops", selectedStructure],
    queryFn: async () => (await api.get("/firma/operatori", { params: { structure_id: selectedStructure } })).data,
  });
  const opsSorted = [...allOps].sort((a, b) => `${a.nome} ${a.cognome}`.localeCompare(`${b.nome} ${b.cognome}`, "it"));

  const toggleId = (id) => setForm((f) => ({ ...f, ids: f.ids.includes(id) ? f.ids.filter((x) => x !== id) : [...f.ids, id] }));
  const saveAdd = async () => {
    if (!form.ids.length) { toast.error("Seleziona almeno un operatore"); return; }
    if (!form.descrizione.trim()) { toast.error("Scrivi la descrizione della task non svolta"); return; }
    setAddBusy(true);
    let ok = 0;
    for (const id of form.ids) {
      try { await api.post("/richiami-manuali", { operatore_id: id, data: form.data, gravita: form.gravita, descrizione: form.descrizione }); ok++; } catch {}
    }
    setAddBusy(false);
    if (ok) toast.success(`Report inviato a ${ok} operator${ok === 1 ? "e" : "i"}`);
    setAddOpen(false);
    setForm({ ids: [], data: new Date().toISOString().slice(0, 10), gravita: "media", descrizione: "" });
    qc.invalidateQueries({ queryKey: ["sup-richiami"] });
  };

  const { data: expData = { richiami: [] }, isLoading: expLoading } = useQuery({
    enabled: !!expanded,
    queryKey: ["sup-richiami-exp", expanded, qDa, qA],
    queryFn: async () => (await api.get("/firma/mio-account/richiami", { params: { op_id: expanded, da: qDa, a: qA } })).data,
  });

  const { data: richMese = { operatori: [] } } = useQuery({
    queryKey: ["sup-richiami-table", selectedStructure, da, a],
    queryFn: async () => (await api.get("/supervisor/richiami", { params: { structure_id: selectedStructure, da, a } })).data,
  });
  const richiamiCounts = {};
  const richiamiDays = {};
  (richMese.operatori || []).forEach((o) => { richiamiCounts[o.nome] = o.conteggi || {}; richiamiDays[o.nome] = o.giorni || {}; });

  // Conteggi "mansioni obbligatorie non svolte" del singolo giorno selezionato.
  const { data: richDay = { operatori: [] } } = useQuery({
    enabled: !!classDay,
    queryKey: ["sup-richiami-day", selectedStructure, classDay],
    queryFn: async () => (await api.get("/supervisor/richiami", { params: { structure_id: selectedStructure, da: classDay, a: classDay } })).data,
  });
  const richiamiCountsDay = {};
  const richiamiDaysDay = {};
  (richDay.operatori || []).forEach((o) => { richiamiCountsDay[o.nome] = o.conteggi || {}; richiamiDaysDay[o.nome] = o.giorni || {}; });

  const { data: turniClassDay = [] } = useQuery({
    enabled: !!classDay,
    queryKey: ["report-op-turni-giorno", selectedStructure, classDay],
    queryFn: async () => (await api.get("/turni", { params: { structure_id: selectedStructure } })).data,
  });
  const turniDelGiorno = (classDay ? turniClassDay : []).filter((t) => t.data === classDay && t.status !== "eliminato");
  const SLOT_LABEL = { mattina: "Mattina", pomeriggio: "Pomeriggio", notte: "Notte", intermedio: "Intermedio", spezzato: "Spezzato" };
  const bySlot = {};
  turniDelGiorno.forEach((t) => { (bySlot[t.turno] = bySlot[t.turno] || []).push(t); });
  const slotKeys = [...new Set([...["mattina", "pomeriggio", "notte", "intermedio", "spezzato"].filter((s) => bySlot[s]), ...Object.keys(bySlot)])];

  return (
    <div className="space-y-5" data-testid="supervisor-richiami-page">
      <div className="flex items-center gap-2 flex-wrap">
        <AlertTriangle size={22} className="text-red-600" />
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-head font-bold">Report operatori</h1>
          <p className="text-sm text-muted-foreground">Quadro complessivo dei report di tutti gli operatori (mancanze automatiche + task non effettuate).</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="sup-richiamo-add"><Plus size={16} className="mr-1" /> Aggiungi report</Button>
      </div>

      <div className="gr-card p-4" data-testid="sup-richiami-tabella">
        <div className="flex items-center gap-2 mb-2">
          <Select value={mese} onValueChange={(m) => { goMonth(m); setClassDay(null); }}><SelectTrigger className="w-[190px] h-9" data-testid="sup-richiami-mese"><SelectValue /></SelectTrigger><SelectContent>{monthOptionsList().map((m) => <SelectItem key={m} value={m}>{meseLabel(m)}</SelectItem>)}</SelectContent></Select>
          <span className="text-xs text-muted-foreground">Attività svolte (classifica) + mansioni obbligatorie non svolte, per operatore</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="report-op-day-selector">
          <button onClick={() => setClassDay(null)} data-testid="report-op-day-tutti"
            className={`h-10 px-3.5 rounded-lg text-sm font-bold border transition-colors ${!classDay ? "bg-primary text-primary-foreground border-primary" : "bg-white hover:bg-secondary"}`}>Tutti</button>
          <button onClick={() => goDay(ieriIsoR)} data-testid="report-op-day-ieri"
            className={`h-10 px-3.5 rounded-lg text-sm font-bold border transition-colors ${classDay === ieriIsoR ? "bg-primary text-primary-foreground border-primary" : "bg-white hover:bg-secondary"}`}>Ieri</button>
          <button onClick={() => goDay(todayIsoR)} data-testid="report-op-day-oggi"
            className={`h-10 px-3.5 rounded-lg text-sm font-bold border transition-colors ${classDay === todayIsoR ? "bg-primary text-primary-foreground border-primary" : "bg-white hover:bg-secondary"}`}>Oggi</button>
          <span className="w-px h-8 bg-border mx-1" />
          {(meseData.giorni || []).map((g) => (
            <button key={g.data} disabled={g.data > todayIsoR} onClick={() => { if (g.data <= todayIsoR) setClassDay(g.data); }} data-testid={`report-op-day-${g.data}`}
              className={`h-10 w-10 rounded-lg border flex flex-col items-center justify-center leading-none transition-colors ${g.data > todayIsoR ? "opacity-40 cursor-not-allowed bg-secondary/40 border-transparent" : classDay === g.data ? "bg-primary text-primary-foreground border-primary" : g.data === todayIsoR ? "bg-white hover:bg-secondary ring-2 ring-primary" : "bg-white hover:bg-secondary"}`}>
              <span className="text-[9px] font-semibold opacity-70">{DOW_ABBR[g.dow]}</span>
              <span className="text-sm font-bold tabnum">{g.giorno}</span>
            </button>
          ))}
        </div>
        {can("disciplina_tabella") && <DisciplinaPanel mese={mese} giorno={classDay} />}
        <ClassificaOperatori mese={mese} giorno={classDay} extraCategorie={RICHIAMI_CATS} extraCounts={classDay ? richiamiCountsDay : richiamiCounts} extraDays={classDay ? richiamiDaysDay : richiamiDays} />
      </div>

      {classDay && (
        <div className="gr-card p-4" data-testid="report-op-turni-giorno">
          <h3 className="font-head font-bold text-base mb-3 flex items-center gap-2"><Clock size={17} className="text-sky-600" /> Chi era in turno · <span className="capitalize font-semibold">{longDate(classDay)}</span></h3>
          {turniDelGiorno.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="report-op-turni-empty">Nessun turno registrato in questo giorno.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {slotKeys.map((k) => (
                <div key={k} className="rounded-xl border bg-white p-3" data-testid={`report-op-turno-slot-${k}`}>
                  <div className="text-xs font-bold uppercase tracking-wide text-sky-700 mb-2">{SLOT_LABEL[k] || k}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {bySlot[k].map((t, i) => {
                      const clickable = !!t.operatore_id;
                      const Tag = clickable ? "button" : "span";
                      return (
                        <Tag key={i} {...(clickable ? { onClick: () => navigate(`/operatori/${t.operatore_id}`), type: "button" } : {})}
                          className={`text-xs font-semibold rounded-full px-2.5 py-1 ${t.leader ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-sky-50 text-sky-800 border border-sky-200"} ${clickable ? "hover:bg-sky-100 hover:underline cursor-pointer transition-colors" : ""}`}>
                          {t.leader && <Medal size={11} className="inline mr-1" style={{ color: "#b45309" }} />}{t.operatore_nome}{t.qualifica ? <span className="font-normal opacity-70"> · {t.qualifica}</span> : ""}
                        </Tag>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dialog aggiungi richiamo */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="sup-richiamo-dialog">
          <DialogHeader><DialogTitle>Aggiungi report · Task non effettuata</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Operatori ({form.ids.length} selezionati)</label>
              <div className="mt-1 max-h-44 overflow-y-auto border rounded-md p-2 flex flex-wrap gap-1.5" data-testid="sup-richiamo-ops">
                {opsSorted.map((op) => {
                  const nm = `${op.nome || ""} ${op.cognome || ""}`.trim();
                  const sel = form.ids.includes(op.id);
                  return (
                    <button key={op.id} type="button" onClick={() => toggleId(op.id)}
                      className={`text-xs font-semibold rounded-full px-2.5 py-1 border transition-colors ${sel ? "bg-primary text-white border-primary" : "bg-white hover:bg-secondary/60"}`}
                      data-testid={`sup-richiamo-op-${op.id}`}>{nm}</button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Giorno</label>
                <Input type="date" value={form.data} onChange={(e) => setForm((f) => ({ ...f, data: e.target.value }))} className="h-9 mt-1" data-testid="sup-richiamo-data" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Gravità</label>
                <Select value={form.gravita} onValueChange={(v) => setForm((f) => ({ ...f, gravita: v }))}>
                  <SelectTrigger className="h-9 mt-1" data-testid="sup-richiamo-gravita"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lieve">Lieve</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="grave">Grave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Descrizione</label>
              <textarea value={form.descrizione} onChange={(e) => setForm((f) => ({ ...f, descrizione: e.target.value }))} rows={4}
                className="w-full mt-1 rounded-md border px-3 py-2 text-sm" placeholder="Descrivi la task non svolta…" data-testid="sup-richiamo-descrizione" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="sup-richiamo-cancel">Annulla</Button>
            <Button onClick={saveAdd} disabled={addBusy} data-testid="sup-richiamo-save">{addBusy ? "Invio…" : `Invia report${form.ids.length > 1 ? ` (${form.ids.length})` : ""}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
